// The IDE shell: composes the store-connected regions into a JetBrains-class
// layout with draggable sidebar / bottom-panel dividers. Every region reads the
// store directly, so this file is pure layout + global keybindings.

import { useCallback, useEffect, useState } from "react";
import { useIde } from "./store";
import { TitleBar } from "./shell/TitleBar";
import { ActivityBar } from "./shell/ActivityBar";
import { StatusBar } from "./shell/StatusBar";
import { Sidebar } from "./sidebar/Sidebar";
import { EditorArea } from "./editor/EditorArea";
import { runEditorCommand } from "./editor/editorHandle";
import { BottomPanel } from "./panels/BottomPanel";
import { CommandPalette } from "./commands/CommandPalette";
import { SettingsModal } from "./settings/SettingsModal";
import { Resizer } from "./shell/Resizer";
import "./styles/app.css";

const SIDEBAR_DEFAULT = 264;
const BOTTOM_DEFAULT = 300;
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const num = (key: string, dflt: number) => {
  const v = Number(localStorage.getItem(key));
  return Number.isFinite(v) && v > 0 ? v : dflt;
};

export function App() {
  const sidebarVisible = useIde((s) => s.sidebarVisible);
  const bottomVisible = useIde((s) => s.bottomVisible);
  const setCommandPalette = useIde((s) => s.setCommandPalette);
  const runCompile = useIde((s) => s.runCompile);
  const toggleBottom = useIde((s) => s.toggleBottom);
  const toggleSidebar = useIde((s) => s.toggleSidebar);

  const [sidebarW, setSidebarW] = useState(() => num("seal-ide.sidebarW", SIDEBAR_DEFAULT));
  const [bottomH, setBottomH] = useState(() => num("seal-ide.bottomH", BOTTOM_DEFAULT));

  useEffect(() => localStorage.setItem("seal-ide.sidebarW", String(sidebarW)), [sidebarW]);
  useEffect(() => localStorage.setItem("seal-ide.bottomH", String(bottomH)), [bottomH]);

  const resizeSidebar = useCallback((d: number) => setSidebarW((w) => clamp(w + d, 190, 560)), []);
  const resizeBottom = useCallback(
    (d: number) => setBottomH((h) => clamp(h - d, 120, window.innerHeight - 220)),
    [],
  );

  // Global keybindings (JetBrains/VS Code lineage).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.shiftKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        setCommandPalette(true);
      } else if (mod && e.key === "Enter") {
        e.preventDefault();
        runCompile("fund");
      } else if (mod && e.key.toLowerCase() === "j") {
        e.preventDefault();
        toggleBottom();
      } else if (mod && e.key.toLowerCase() === "b") {
        e.preventDefault();
        toggleSidebar();
      } else if (mod && !e.shiftKey && e.key.toLowerCase() === "p") {
        // Also stops the browser print dialog, which is a rude surprise here.
        e.preventDefault();
        setCommandPalette(true);
      } else if (mod && e.key.toLowerCase() === "g") {
        e.preventDefault();
        runEditorCommand("editor.action.gotoLine");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setCommandPalette, runCompile, toggleBottom, toggleSidebar]);

  return (
    <div className="ide">
      <TitleBar />
      <div className="ide-body">
        <ActivityBar />
        {sidebarVisible && (
          <>
            <div className="ide-sidebar" style={{ width: sidebarW }}>
              <Sidebar />
            </div>
            <Resizer
              orientation="vertical"
              onResize={resizeSidebar}
              onResetDefault={() => setSidebarW(SIDEBAR_DEFAULT)}
            />
          </>
        )}
        <div className="ide-main">
          <div className="ide-editor">
            <EditorArea />
          </div>
          {bottomVisible && (
            <>
              <Resizer
                orientation="horizontal"
                onResize={resizeBottom}
                onResetDefault={() => setBottomH(BOTTOM_DEFAULT)}
              />
              <div className="ide-bottom" style={{ height: bottomH, flex: "none" }}>
                <BottomPanel />
              </div>
            </>
          )}
        </div>
      </div>
      <StatusBar />
      <CommandPalette />
      <SettingsModal />
    </div>
  );
}
